import { useState, useContext, useEffect, HTMLAttributes } from "react";
import ContractContext, {
  useRoleName,
  useRole,
  useContract,
} from "../ContractContext";
import {
  ContractField,
  ContractFieldWrapper,
} from "../fields/ContractFieldInput";
import { FieldInput, FieldType } from "../fields/FieldInput";
import { BUYER } from "../roles";
import CardSection from "../../formatting/CardSection";
import InputValidationContext, {
  PatternValidationParameters,
  useInputValidation,
  ValidationErrorResult,
} from "../validation/InputValidationContext";
import { ILLUSTRATIONS } from "../../formatting/Illustration";
import ContractFieldsView from "../fields/ContractFieldsView";
import ROLES from "../roles";
import { useContractClient } from "../../api/ContractClient";
import InfoBox from "../../shared/elements/InfoBox";
import {
  ContractingParty,
  ContractViewModel,
  Role,
} from "../model/ContractViewModel";
import { validateOrganizationNumber } from "../fields/OrganizationNumber";
import { CoOwnerCard } from "./CoOwnerCard";
import styled from "styled-components";
import { validateNationalIdentityNumber } from "../insurance/validateNationalIdentityNumber";
import { onDesktop } from "../../shared/layout/Breakpoints";
import ContractCheckboxField from "../fields/form-controls/ContractCheckboxField";

export const EMAIL_PATTERN =
  '^(([^<>()\\[\\]\\\\.,;:\\s@"]+(\\.[^<>()\\[\\]\\\\.,;:\\s@"]+)*)|(".+"))@((\\[[0-9]{1,3}\\.[0-9]{1,3}\\.[0-9]{1,3}\\.[0-9]{1,3}])|(([a-zA-Z\\-0-9]+\\.)+[a-zA-Z]{2,}))$';

interface FieldValue {
  stringValue?: string;
}
interface Field {
  name: keyof ContractingParty;
  type?: FieldType;
  autocomplete?: string;
  required?: boolean;
  labelHint?: string;
  pattern?: PatternValidationParameters;
  format(value: string): string;
  toFieldValue?: (rawValue: string) => FieldValue;
  validate?: (value: string | null) => ValidationErrorResult | null;
}

const CO_OWNER_FIELDS: Field[] = [
  {
    name: "phone",
    type: "phone",
    format: (value) => value || "-",
  },
  {
    name: "firstName",
    format: (value) => value || "-",
  },
  {
    name: "lastName",
    format: (value) => value || "-",
  },
  {
    name: "email",
    type: "email",
    format: (value) => value || "-",
  },
];

const CONTACT_INFO_FIELDS: (contract: ContractViewModel) => Field[] = (c) => {
  const source = [
    {
      name: "phone",
      type: "phone",
      //autocomplete: "tel",
      required: true,
      pattern: {
        match: "^((\\+|00)47\\s?)?\\d{3}\\s?\\d{2}\\s?\\d{3}$",
        error: "Fyll inn et gyldig norsk telefonnummer",
      },
      format: (value) => value || "-",
    },
    {
      name: "firstName",
      //autocomplete: "given-name",
      required: true,
      format: (value) => value || "-",
    },
    {
      name: "lastName",
      //autocomplete: "family-name",
      required: true,
      format: (value) => value || "-",
    },
    {
      name: "address",
      //autocomplete: "street-address",
      required: true,
      format: (value) => value || "-",
    },
    {
      name: "postalCode",
      type: "tel",
      //autocomplete: "postal-code",
      required: true,
      format: (value) => value || "-",
    },
    {
      name: "city",
      //autocomplete: "address-level2",
      required: true,
      format: (value) => value || "-",
    },
    {
      name: "email",
      type: "email",
      //autocomplete: "email",
      required: true,
      format: (value) => value || "-",
      pattern: {
        match: EMAIL_PATTERN,
        error: "Fyll inn en gyldig e-postadresse",
      },
    },
    c.ownershipTransfer?.featureEnabled
      ? {
          name: "nationalIdentityNumber",
          type: "tel",
          autocomplete: "off",
          format: (value) => value || "-",
          validate: (value) => {
            if (!value) return null;

            const result = validateNationalIdentityNumber(value);

            if (result.valid) return null;

            return { patternError: "Fyll inn et gyldig fødselsnummer" };
          },
        }
      : null,
    {
      name: "accountNumber",
      type: "account",
      autocomplete: "off",
      toFieldValue: (stringValue) => ({ stringValue }),
      format: (value) => value || "-",
    },
  ] as (Field | null)[];

  return source.filter(notEmpty);
};

function notEmpty<TValue>(value: TValue | null | undefined): value is TValue {
  return value !== null && value !== undefined;
}

export const AdditionalFieldDescription = styled.p`
  margin-top: -2em;
  margin-bottom: 2em;
  font-size: 0.75em;
  padding 0 ${({ theme }) => theme.spacing.space8};
  color: ${({ theme }) => theme.nafColor.signature.black};
`;

const CompanyInfo = styled(InfoBox)`
  margin-left: 1em;
  margin-right: 1em;
  margin-top: -1em;
`;

const ContactInfoForm = styled(function ({
  autofillCustomer,
  section,
  className,
  ...props
}: {
  autofillCustomer: (phone: string) => Promise<ContractingParty | null>;
  section: "buyer" | "seller";
} & HTMLAttributes<HTMLDivElement>) {
  const contractContext = useContext(ContractContext);
  if (!contractContext) throw new Error("No ContractContext found!");
  const { saveFields, saveField } = contractContext;
  const contract = useContract();

  const party = contract[section];
  const values = (party || {}) as ContractingParty;
  const storedPhoneValue = values.phone;
  const [phoneValue, setPhoneValue] = useState(storedPhoneValue || null);
  const role = useRole();

  useEffect(() => {
    setPhoneValue(storedPhoneValue || null);
  }, [storedPhoneValue]);

  const isOrganization = party?.isOrganization;
  const storedOrganizationNumberValue = values.organizationNumber;
  const [organizationNumberValue, setOrganizationNumberValue] = useState(
    storedOrganizationNumberValue || null
  );

  useEffect(() => {
    setOrganizationNumberValue(storedOrganizationNumberValue || null);
  }, [storedOrganizationNumberValue]);

  const {
    validate: validateOrganizationNumberRequired,
    error: organizationNumberError,
  } = useInputValidation(section, "organizationNumber", {
    required: true,
    callback: function (value: string | null) {
      if (!value) return null;

      const x = validateOrganizationNumber(value);

      if (x.valid) return null;

      return {
        patternError: "Dette ser ikke ut som et gyldig organisasjonsnummer",
      };
    },
  });

  const contactInfoFields = CONTACT_INFO_FIELDS(contract);

  const phoneInput = contactInfoFields.filter(
    ({ name }) => name === "phone"
  )[0];
  const nationalIdentityNumberInput = contactInfoFields.filter(
    ({ name }) => name === "nationalIdentityNumber"
  )[0];
  const inputs = contactInfoFields.filter(
    ({ name }) =>
      name !== phoneInput.name && name !== nationalIdentityNumberInput.name
  );

  const inputValidationContext = useContext(InputValidationContext);

  if (!inputValidationContext)
    throw new Error("No InputValidationContext found!");

  const { validateField } = inputValidationContext;

  const { validate: validatePhone, error: phoneError } = useInputValidation(
    section,
    phoneInput.name,
    {
      required: phoneInput.required,
      pattern: phoneInput.pattern,
    }
  );

  async function autofill(phone: string) {
    if (!(phone && new RegExp(phoneInput.pattern!.match).test(phone))) return;

    // Don't autofill if all fields are filled
    if (contactInfoFields.filter(({ name: key }) => !values[key]).length === 0)
      return;

    let autofillResult: ContractingParty | null = null;

    try {
      autofillResult = await autofillCustomer(phone);
    } catch (autofillError) {}

    if (autofillResult) {
      const updates = (
        Object.keys(autofillResult) as (keyof ContractingParty)[]
      )
        .filter((key) => !values[key] && autofillResult![key])
        .filter((key) => key !== "autoFilled") // Don't autofill the "autoFilled" key because this is used for server-side auto-filling
        .filter((key) => key !== "updated") // Don't autofill the "updated" key because this is not updated manually
        .filter((key) => key !== "phone") // Don't autofill the "phone" key because we used that to search and it is already saved and formatted
        .map((field) => {
          const value = autofillResult![field] as string;

          return {
            section,
            field,
            value: value,
            fieldValue: { stringValue: value },
          };
        });

      await saveFields(updates);

      for (const update of updates) {
        const contactInfoField = contactInfoFields.filter(
          ({ name }) => update.field === name
        )[0];

        validateField(section, contactInfoField, update.value);
      }
    }
  }

  const isOrganizationLabel =
    role === Role.Seller
      ? "Bilen selges på vegne av firma"
      : "Bilen kjøpes på vegne av firma";

  return (
    <div
      className={["ContactInfoForm", className].filter((c) => c).join(" ")}
      {...props}
    >
      <ContractCheckboxField
        section={section}
        name="isOrganization"
        label={isOrganizationLabel}
      />
      {isOrganization ? (
        <>
          <CompanyInfo>
            Kjøpekontrakten må signeres av firmaets prokurist, det vil si de(n)
            personen(e) som har fullmakt til å signere på vegne av firma.
          </CompanyInfo>
          <ContractFieldWrapper
            section={section}
            name="organizationNumber"
            type="text"
            required
            hasError={!!organizationNumberError}
          >
            <FieldInput
              section={section}
              name="organizationNumber"
              type="text"
              required
              value={{
                current: organizationNumberValue,
                onChange: (value: string) => setOrganizationNumberValue(value),
                onSave: async (value: string) => {
                  if (value === storedPhoneValue) return;

                  validateOrganizationNumberRequired(value);

                  await saveField(
                    section,
                    "organizationNumber",
                    value,
                    value
                      ? {
                          stringValue: value,
                        }
                      : null
                  );
                },
              }}
            />
          </ContractFieldWrapper>
          <ContractField
            section={section}
            name="organizationName"
            type="text"
          />
        </>
      ) : null}
      <ContractFieldWrapper
        key={phoneInput.name}
        section={section}
        name={phoneInput.name}
        type={"phone"}
      >
        <FieldInput
          section={section}
          name={phoneInput.name}
          type={"phone"}
          required={phoneInput.required}
          inputProps={{ autoComplete: phoneInput.autocomplete }}
          hasError={!!phoneError}
          value={{
            current: phoneValue,
            onChange: (value: string) => setPhoneValue(value),
            onSave: async (value: string) => {
              if (value === storedPhoneValue) return;

              if (value?.substring(0, 3) === "+47") {
                validatePhone(value);
              }

              await saveField(
                section,
                phoneInput.name,
                value,
                value
                  ? {
                      stringValue: value,
                    }
                  : null
              );

              await autofill(value);
            },
          }}
        />
      </ContractFieldWrapper>
      {inputs
        .filter((field) => {
          if (field.name === "accountNumber" && role === ROLES.BUYER) {
            return false;
          }
          return true;
        })
        .map((field) => (
          <ContractField
            key={field.name}
            section={section}
            name={field.name}
            type={field.type || "text"}
            required={field.required}
            inputProps={{
              autoComplete: field.autocomplete,
            }}
            validation={{
              pattern: field.pattern,
              required: field.required,
              callback: field.validate,
            }}
            labelHint={field.labelHint}
          />
        ))}
      <ContractField
        section={section}
        name={nationalIdentityNumberInput.name}
        type={nationalIdentityNumberInput.type || "text"}
        required={nationalIdentityNumberInput.required}
        inputProps={{
          autoComplete: nationalIdentityNumberInput.autocomplete,
        }}
        validation={{
          pattern: nationalIdentityNumberInput.pattern,
          required: nationalIdentityNumberInput.required,
          callback: nationalIdentityNumberInput.validate,
        }}
        labelHint={nationalIdentityNumberInput.labelHint}
      />
      <AdditionalFieldDescription>
        Fylles ut dersom du ønsker automatisk eierskifte hos Statens vegvesen
      </AdditionalFieldDescription>
    </div>
  );
})`
  display: flex;
  flex-wrap: wrap;
  margin: 0 -${(props) => props.theme.spacing.space8};

  .FormGroup {
    width: 100%;
    padding: 0 ${(props) => props.theme.spacing.space8};
    box-sizing: border-box;
  }

  ${onDesktop`
    .FormGroup.ContractField-city,
    .FormGroup.ContractField-postalCode {
      width: 50%;
    }

    .FormGroup.ContractField-firstName,
    .FormGroup.ContractField-lastName {
      width: 50%;
    }
  `}
`;

const CoOwnerView = styled.div`
  h2 {
    text-align: center;
  }
  margin-top: 1em;
`;

export default function ContactInfoSection() {
  const client = useContractClient();
  const contract = useContract();
  const role = useRole();
  const section = role === BUYER ? "buyer" : "seller";
  const coOwnerSection = role === BUYER ? "buyerCoOwner" : "sellerCoOwner";
  const contractingParty = contract[section] || ({} as ContractingParty);
  const coOwner = contract[coOwnerSection];
  const { autoFilled } = contractingParty;
  const { self } = useRoleName();

  return (
    <CardSection
      title="Dine opplysninger"
      ingress={`Fyll ut opplysninger om deg som ${self}.`}
      illustration={
        role === BUYER ? ILLUSTRATIONS.SELFIE_WOMAN : ILLUSTRATIONS.SELFIE_MAN
      }
    >
      {contract.signature ? (
        <>
          <ContractFieldsView
            section={section}
            fields={CONTACT_INFO_FIELDS(contract)}
          />
          {coOwner?.included ? (
            <CoOwnerView>
              <h2>Medeiers opplysninger</h2>
              <ContractFieldsView
                section={coOwnerSection}
                fields={CO_OWNER_FIELDS}
              />
            </CoOwnerView>
          ) : null}
        </>
      ) : (
        <>
          {autoFilled ? (
            <InfoBox>
              Vi har fylt inn informasjon basert på ditt mobilnummer. Sjekk at
              den stemmer og fyll inn e-postadresse.
            </InfoBox>
          ) : null}
          <ContactInfoForm
            section={section}
            autofillCustomer={(phone) =>
              client.get<ContractingParty>(`customer/autofill?phone=${phone}`)
            }
          />
          <CoOwnerCard />
        </>
      )}
    </CardSection>
  );
}
